import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import SwipeableViews from 'react-swipeable-views';
import { DialogTitle, DialogContent, Dialog, Button, DialogActions, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TFunction } from 'i18next';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import DialogDataSelection from '@/components/UI/DialogDataSelection/DialogDataSelection';
import Dropdown from '@/components/UI/Dropdown/Dropdown';
import InputText from '@/components/UI/InputText/InputText';
import InputUser from '@/components/UI/InputUser/InputUser';
import ValueByMBOSLabel from '@/components/UI/TableListEditable/ValueByMBOSLabel/ValueByMBOSLabel';
import TabsCampaignsSubCR from '@/containers/SubCampaignDetail/Feedback/TabsCampaignsSubCR';
import { YES, formYESNO, EMPTY_USERINFO_OBJECT, fieldsFromDialog, tablesWithAddDialogInfo, errorTypes, YWP_VERSION_NEW, YWP_VERSION_OLD, fieldsFromMBOS, tablesWithAddMBOSInfo, TABLE_FIELD_MAX_LENGTH, codingParts, codingUuids, SOFTWARE, uuidCodeOld, HARDWARE_WITHPART_TABLE_ID, HARDWARE_WITHOUTPART_TABLE_ID, ERROR } from '@/helpers/constants';
import { processEditFieldSynchronization } from '@/helpers/tables-utils';
import { isCampaignRDMP, isFlashableRelease, isMbos, isPDRSCreatedCampaign, isValidPartNumber, isValidQeLevel, isValidYWPFortmat, userSearchToUserInfo } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import { getCampaignInfo, getCampaignSoftware } from '@/redux/Campaign/campaign.reducer';
import { getSoftwareForms } from '@/redux/Forms/forms.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import campaignServices from '@/services/campaignServices';
import { ReduxForms, AffectedSoftware, UserInfo, CampaignInfo, UserSearch, MBOSPartNumberInfoResponse, MBOSSynchronization } from '@/types';
import ValueByDIA4ULabel from '../ValueByDIA4ULabel/ValueByDIA4ULabel';
import styles from './DialogTableList.module.scss';

const useStyles = makeStyles(() => ({
  content: {
    paddingTop: 0,
    paddingBottom: 0,
    width: '800px',
    marginTop: '-20px',
  },
  inputLabelTooltip: {
    overflow: 'unset',
  },
  customContentStyle: {
    paddingTop: 0,
    width: '825px',
    minHeight: '35vw',
  },
}));

const isSoftware = (detail) => `software-${detail}`;

const softwareCodingPartsTableId = isSoftware(codingParts);
const softwareCodingUuidsTableId = isSoftware(codingUuids);
const softwareFlashingPartsTableId = isSoftware(`${SOFTWARE.FLASHING}Parts`);
const softwareFlashingUuidsTableId = isSoftware(`${SOFTWARE.FLASHING}Uuids`);
const softwareDoormanPartsTableId = isSoftware('doormanParts');
const softwareDoormanUuidsTableId = isSoftware('doormanUuids');
const softwareBlockVehicleTableId = isSoftware('blockVehicle');

const FIELD_DESCRIPTION = 'description';
const FIELD_CODE_CONDITION = 'codeCondition';
const FIELD_FREE_TEXT = 'freeText';
const FIELD_ADDITIONAL_INFORMATION = 'additionalInformation';
const FIELD_BTV_PERSON = 'btvPerson';
const FIELD_UUID_PERSON_RESPONSIBLE = 'uuidPersonResponsible';
const FIELD_QE_LEVEL = 'qeLevel';
const FIELD_TYPE_APPROVAL_NUMBER = 'typeApprovalNumber';
const FIELD_DIOGENES_NAME = 'diogenesName';
const FIELD_NUMBER_NEW = 'numberNew';
const FIELD_UUID_CODE = 'uuidCode';

const softwareTables = [softwareCodingPartsTableId, softwareCodingUuidsTableId, softwareFlashingPartsTableId, softwareFlashingUuidsTableId,
  softwareDoormanPartsTableId, softwareDoormanUuidsTableId];
const tablesWithBTVPerson = [softwareCodingPartsTableId, softwareCodingUuidsTableId, softwareFlashingPartsTableId, softwareFlashingUuidsTableId,
  HARDWARE_WITHOUTPART_TABLE_ID, HARDWARE_WITHPART_TABLE_ID];

const moreWidthFields = [FIELD_DESCRIPTION, FIELD_CODE_CONDITION, FIELD_FREE_TEXT, FIELD_ADDITIONAL_INFORMATION, FIELD_BTV_PERSON,
  FIELD_UUID_PERSON_RESPONSIBLE, FIELD_QE_LEVEL, FIELD_TYPE_APPROVAL_NUMBER, FIELD_DIOGENES_NAME];
const fieldsWithoutSpaces = [FIELD_NUMBER_NEW, FIELD_UUID_CODE];

interface TableDialogRow {
  btvPerson?: UserInfo;
  uuidPersonResponsible?: UserInfo;
  mbosSynchronization?: MBOSSynchronization;
  [fieldKey: string]: string | UserInfo | MBOSSynchronization | undefined;
}

interface DialogTableListProps {
  close: () => void;
  title: string;
  tableDialogRows: TableDialogRow[];
  tableDialogTitles: { [fieldKey: string]: string };
  onSave: (rowData: [{ [fieldKey: string]: string }]) => void;
  table: string;
  software: AffectedSoftware;
  softwareForm: ReduxForms;
  headerLanguage: string;
  campaignInfo: CampaignInfo;
}

const getDialogSyncProperty = (newData) => {
  const fieldsSynchronized = fieldsFromDialog.filter((field) => newData?.[field]);

  return {
    dialogSynchronization: {
      updatedByDialog: fieldsSynchronized?.length > 0,
      dialogUpdateDate: fieldsSynchronized?.length > 0 ? (new Date().toISOString()) : '',
      dialogFieldsSynchronized: fieldsSynchronized,
      dialogUpdateInitialData: fieldsSynchronized.reduce((acc, field) => {
        acc[field] = newData[field];

        return acc;
      }, {}),
    },
  };
};

const dialogDataToColumnFilters = (dialogData, columnFilters, index: number) => {
  let newRows: any = [];

  dialogData.forEach((data, i) => {
    const newData = {
      ...(columnFilters[index]),
      description: data?.description ?? '',
      modelSeries: data?.modelSeries ?? '',
      typeVersions: data?.typeVersion ?? '',
      codeCondition: Array.isArray(data?.codeRule) ? (data?.codeRule?.[i] ?? '') : data?.codeRule ?? '',
      btvPerson: { // is btvPerson always when is table with dialog button
        ...EMPTY_USERINFO_OBJECT,
        ...data?.personResponsible,
      },
    };
    const dialogSynchronization = getDialogSyncProperty(newData);

    newRows = [...newRows, { ...newData, ...dialogSynchronization }];
  });

  return newRows;
};

const createSyncFieldLabel = (label: string, fieldName: string, syncInfo, t: TFunction) => {
  const isFromDialog = syncInfo?.dialogSynchronization?.dialogFieldsSynchronized?.includes(fieldName);
  const isFromMBOS = syncInfo?.mbosSynchronization?.synchronizedFields?.includes(fieldName);

  if (!isFromDialog && !isFromMBOS) return t(label);

  return (
    <>
      {t(label)}
      {isFromDialog ? <ValueByDIA4ULabel /> : <ValueByMBOSLabel />}
    </>
  );
};

const mbosDataToColumnFilters = (mbosData: TableDialogRow[], columnFilters: TableDialogRow[], index: number): TableDialogRow[] => {
  let newRows: TableDialogRow[] = [];

  mbosData.forEach((data) => {
    const newData = {
      ...(columnFilters[index]),
      description: data?.description ?? '',
      ...(('uuidCode' in columnFilters[index]) && { uuidCode: data?.uuidCode ?? '' }),
    };
    const synchronizedFields = fieldsFromMBOS.filter((field) => newData?.[field]);
    const mbosSynchronization: MBOSSynchronization = {
      isSynchronized: synchronizedFields?.length > 0,
      synchronizedFields,
      synchronizedData: {
        description: data.description as string | undefined,
        uuidCode: data.uuidCode as string | undefined,
      },
    };

    newRows = [...newRows, { ...newData, mbosSynchronization }];
  });

  return newRows;
};

const DialogTableList: React.FC<DialogTableListProps> = (props) => {
  const { close, title, tableDialogRows, tableDialogTitles, onSave, table, software, softwareForm, headerLanguage,
    campaignInfo } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const theme: any = useTheme();
  const { addToast } = useToastContext();
  const { setLoading } = useGlobalSpinnerContext();
  const fetchDialogInfo = useApiRequest(campaignServices.getDialogInformation);
  const [columnFilters, setColumnFilters] = useState(tableDialogRows);
  const [dialogData, setDialogData] = useState<any>(null);
  const [combinationSelectionOpen, setCombinationSelectionOpen] = useState(false);
  const [numberNewNotFound, setNumberNewNotFound] = useState(false);
  const [columnValidations, setColumnValidations] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [showConfirmDeleteCR, setShowConfirmDeleteCR] = useState<number | null>(null);
  const [validationActive, setValidationActive] = useState(false);
  const [mbosPartNumberInfo, setMbosPartNumberInfo] = useState<MBOSPartNumberInfoResponse | null>(null);
  const [newNumberExist, setNewNumberExist] = useState<boolean | null>(null);
  const [oldNumberExist, setOldNumberExist] = useState<boolean | null>(null);
  const [uuidNumberExist, setUuidNumberExist] = useState<boolean | null>(null);
  const { otaUpdate, mbosDetails } = software || {};
  const isDialogTable = tablesWithAddDialogInfo.includes(table);
  const isSoftwareTable = softwareTables.includes(table);
  const isCodingPartsTable = table === softwareCodingPartsTableId;
  const isCodingUuidTable = table === softwareCodingUuidsTableId;
  const isFlashingUuidTable = table === softwareFlashingUuidsTableId;
  const isFlashingTable = table === softwareFlashingPartsTableId;
  const isDoormanTable = table === softwareBlockVehicleTableId;
  const isMBOSTable = tablesWithAddMBOSInfo.includes(table);
  const isRDMP = isCampaignRDMP(campaignInfo?.creationUser?.department);
  const isTableWithBTVPerson = tablesWithBTVPerson.includes(table) && isRDMP;
  const userField = tableDialogRows?.[0] && 'btvPerson' in tableDialogRows[0] ? 'btvPerson' : 'uuidPersonResponsible';
  const handleClose = (reason: string) => (reason && reason === 'backdropClick' && close());
  const NUMBER_NEW: string = 'numberNew';
  const NUMBER_OLD: string = 'numberOld';
  const isMbosRelevant = isMbos(mbosDetails);
  const partNumberNeedsFormat = isSoftwareTable && isMbosRelevant;
  const isFlashableReleaseSelected = isFlashableRelease(mbosDetails);
  const isPDRS = isPDRSCreatedCampaign(campaignInfo);
  const isPDRSIdDisabled = (elem) => elem === NUMBER_OLD && isPDRS && (table.includes('hardware') || table.includes('software'));
  const currentRow = columnFilters?.[currentIndex];
  const { numberOld: currentIdxOldPartNumber, ywpVersionOld: currentIdxOldYwp, ywpVersionNew: currentNewYwp, uuidCodeOld: uuidCodeOldData } = currentRow || {};
  const isEqualPartNumbers = currentRow?.numberNew && currentRow?.numberOld
    && currentRow?.numberNew === currentRow?.numberOld;
  const partNumber = isPDRS ? currentRow?.numberNew : currentIdxOldPartNumber;

  const disableAddButton = columnFilters?.some((elem) => {
    const { diogenesName, hwPartNo, numberNew, numberOld, vpd } = elem;

    const isOTACoding = isCodingPartsTable && otaUpdate === YES;
    const isOTAFlashing = isFlashingTable && otaUpdate === YES;
    const isMBOSFlashing = (isFlashingTable || isCodingPartsTable) && isMbosRelevant;
    const isHardwareTable = !isSoftwareTable;

    const codingFieldsEmpty = !diogenesName || !numberNew || !hwPartNo;
    const flashingOTAFieldsEmpty = !numberOld || (!isMbosRelevant && codingFieldsEmpty);
    const flashingMBOSFieldsEmpty = isFlashingTable ? !numberOld : (!numberNew || !numberOld);
    const vpdNotSelected = vpd === '';

    const codingInvalid = isOTACoding && codingFieldsEmpty;
    const flashingInvalid = isOTAFlashing ? flashingOTAFieldsEmpty : isMBOSFlashing && flashingMBOSFieldsEmpty;
    const vpdInvalid = isHardwareTable && vpdNotSelected;
    const checkOldPartNumber = isFlashableReleaseSelected && !isCodingUuidTable && (isPDRS ? !newNumberExist : !oldNumberExist);
    const checkUuiOldNumber = isFlashableReleaseSelected && isCodingUuidTable && !uuidNumberExist;

    return checkOldPartNumber || checkUuiOldNumber || codingInvalid || flashingInvalid || vpdInvalid;
  });

  const inputIsMandatory = (elem: string) => {
    const mandatoryMBOSInputs = ['numberOld', 'numberNew'];
    const softwareCodingMandatoryInputs = (isCodingPartsTable && otaUpdate === YES
      && ['diogenesName', 'numberNew', 'hwPartNo'].includes(elem));
    const softwareCodingMandatoryMBOSInputs = (isCodingPartsTable && isMbosRelevant && mandatoryMBOSInputs.includes(elem));
    const softwareFlashingMandatoryMBOSInputs = (isFlashingTable && isMbosRelevant && elem === NUMBER_OLD);
    const softwareFlashingMandatoryOTAInputs = (isFlashingTable && otaUpdate === YES && !isMbosRelevant
      && ['numberOld', 'numberNew', 'diogenesName', 'hwPartNo'].includes(elem));
    const softwareBlockMandatoryInputs = (isDoormanTable && [codingParts, `${SOFTWARE.FLASHING}Parts`, 'doormanParts'].includes(elem));
    const hardwareTablesVPDMandatory = (!isSoftwareTable && elem === 'vpd');
    const inputYWPMandatory = [YWP_VERSION_OLD, YWP_VERSION_NEW].includes(elem) && isEqualPartNumbers && isMbosRelevant;
    const mandatoryUUIDOld = (isCodingUuidTable || isFlashingUuidTable) && isFlashableReleaseSelected && elem === uuidCodeOld;

    return softwareCodingMandatoryInputs || softwareCodingMandatoryMBOSInputs || softwareBlockMandatoryInputs
      || softwareFlashingMandatoryMBOSInputs || softwareFlashingMandatoryOTAInputs || hardwareTablesVPDMandatory || inputYWPMandatory || mandatoryUUIDOld;
  };

  const dialogDataColumns = useMemo(() => [
    { name: 'codeRule', title: t('software.codeCondition'), wrapText: true },
    { name: 'modelSeries', title: t('software.modelSeries'), wrapText: true },
    { name: 'typeVersion', title: t('software.typeVersions'), wrapText: true },
  ], [t]);

  const getColumnValidations = (newData) => {
    const newColumnValidations = newData?.map((row) => {
      const invalidFields = Object.keys(row).reduce((acc, elem) => {
        if (inputIsMandatory(elem) && row[elem] === '') acc[elem] = { invalid: true };

        return acc;
      }, {});

      return { ...invalidFields };
    });

    return newColumnValidations;
  };

  const handleEdit = (inputName: string, textInput: string | UserInfo, index: number) => {
    let value = (fieldsWithoutSpaces.includes(inputName) && typeof textInput === 'string')
      ? textInput.replace(/\s/g, '')
      : textInput;

    if ([YWP_VERSION_OLD, YWP_VERSION_NEW].includes(inputName) && typeof value === 'string') {
      value = value.replace(/\D/g, '');

      if (value?.length <= 6) {
        value = value.match(/.{1,2}/g)?.join('/') || value;
      } else {
        return;
      }
    }

    let newData = columnFilters?.map((elem, i) => (i === index ? { ...elem, [inputName]: value } : elem));
    newData = newData?.map((row, i) => (i === index ? processEditFieldSynchronization(row) : row));
    setColumnFilters(newData);

    if (inputIsMandatory(inputName)) setColumnValidations(getColumnValidations(newData));
    if (inputName === NUMBER_NEW) setNumberNewNotFound(false);
  };

  const updateUser = (label: string, user: UserSearch | null, index) => {
    handleEdit(label, userSearchToUserInfo(user), index);
  };

  const getInputPlaceHolder = (inputName: string) => {
    if (inputName === FIELD_QE_LEVEL) return t('hardware.qeLevelPlaceholder');
    if (inputName === NUMBER_NEW && partNumberNeedsFormat) return t('validations.partNumberFormat');
    if (inputIsMandatory(inputName)) return t('validations.required');

    return '';
  };

  const displayServerError = (error) => {
    if (error?.code === errorTypes.MBOS_SERVER_ERROR) {
      addToast(t('dialog.createMBOSCrError'), ERROR);
    }
  };

  // Add with "add button"
  useEffect(() => {
    const partNumberPattern = /^[A-Z]\d{10}$/;
    const partNumber = isPDRS ? currentRow?.numberNew : currentIdxOldPartNumber;

    if (isFlashableReleaseSelected && partNumber && partNumberPattern.test(partNumber as string)) {
      const fetchPartNumberNewCheck = campaignServices.checkOldNumberExist(partNumber);
      checkValueExist(fetchPartNumberNewCheck).then((res) => {
        if (isPDRS) {
          setNewNumberExist(res.exists);
        } else {
          setOldNumberExist(res.exists);
        }

        if (!res.exists) {
          addToast(t('dialog.SNRNotExists'), ERROR);
        }
      }).catch((error) => {
        setNewNumberExist(null);
        setOldNumberExist(null);
        displayServerError(error);
      });
    } else {
      setNewNumberExist(null);
      setOldNumberExist(null);
    }
  }, [currentIdxOldPartNumber, currentRow?.numberNew, isFlashableReleaseSelected, isMbosRelevant, isPDRS]);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    if (isFlashableReleaseSelected && uuidCodeOldData && uuidPattern.test(uuidCodeOldData as string)) {
      const fetchUuidCheck = campaignServices.checkUuidCode(uuidCodeOldData);
      checkValueExist(fetchUuidCheck).then((res) => {
        setUuidNumberExist(res.exists);

        if (!res.exists) {
          addToast(t('dialog.uuidNotExists'), ERROR);
        }
      }).catch((error) => {
        setUuidNumberExist(null);
        displayServerError(error);
      });
    } else {
      setUuidNumberExist(null);
    }
  }, [uuidCodeOldData, isFlashableReleaseSelected, isMbosRelevant]);// eslint-disable-line react-hooks/exhaustive-deps

  // Add with "MBOS button"
  useEffect(() => {
    const checkMbosPartNumberInfo = async () => {
      try {
        setLoading(true);
        const response: MBOSPartNumberInfoResponse = await campaignServices.getMbosPartNumberInfo(partNumber, currentIdxOldYwp);
        setMbosPartNumberInfo(response);
      } catch (error) {
        setMbosPartNumberInfo(null);
        addToast((error as { code?: string }).code === errorTypes.NON_EXISTING_MBOS_PART
          ? t('errors.nonExistingMBOSPart', { id: partNumber, version: currentIdxOldYwp })
          : t('errors.errorBoundaryMessage1'),
        ERROR);
      } finally {
        setLoading(false);
      }
    };

    if (!isMbosRelevant) return;

    if (!partNumber || !currentIdxOldYwp) {
      setMbosPartNumberInfo(null);
      setColumnFilters((prev) => mbosDataToColumnFilters([{ description: '', uuidCode: '' }], prev, currentIndex));

      return;
    }

    checkMbosPartNumberInfo();
  }, [currentIdxOldPartNumber, isPDRS, currentRow?.numberNew, currentIdxOldYwp, isMbosRelevant]); // eslint-disable-line react-hooks/exhaustive-deps

  const setMbosFieldsInfo = (): void => {
    if (!mbosPartNumberInfo?.description && !mbosPartNumberInfo?.uuidCode) return;
    setColumnFilters((prev) => mbosDataToColumnFilters(
      [{ description: mbosPartNumberInfo.description, uuidCode: mbosPartNumberInfo.uuidCode }],
      prev,
      currentIndex,
    ));
  };

  const getDialogInformation = () => {
    setLoading(true);
    fetchDialogInfo(currentRow.numberNew, headerLanguage)
      .then((dialogInfo) => {
        setDialogData(dialogInfo);

        if (dialogInfo.codeRule?.length > 1) {
          setCombinationSelectionOpen(true);
        } else {
          const dialogInfoNormalized = [{
            ...dialogInfo,
            numberNew: currentRow?.numberNew,
            modelSeries: dialogInfo?.codeRule?.[0]?.modelSeries ?? '',
            typeVersion: dialogInfo?.codeRule?.[0]?.typeVersion ?? '',
            codeRule: dialogInfo?.codeRule?.[0]?.codeRule ?? '',
          }];
          setColumnFilters(dialogDataToColumnFilters(dialogInfoNormalized, columnFilters, currentIndex));
        }
        setNumberNewNotFound(false);
      })
      .catch(() => setNumberNewNotFound(true))
      .finally(() => setLoading(false));
  };

  const saveDialogDataInfo = (dataSelected) => {
    const dialogInfoNormalized = dataSelected?.map((elem) => ({
      ...dialogData,
      ...elem,
      description: dialogData?.description,
    }));
    setColumnFilters(dialogDataToColumnFilters(dialogInfoNormalized, columnFilters, currentIndex));
    setCombinationSelectionOpen(false);
  };

  const checkValueExist = async (validationValue) => {
    try {
      setLoading(true);

      return await validationValue;
    } catch (error: unknown) {
      if ((error as { code?: string })?.code !== errorTypes.MBOS_SERVER_ERROR) {
        addToast(error instanceof Error ? error.message : t('errors.errorBoundaryMessage1'), ERROR);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const onSaveRow = () => {
    const newRows: any = [...columnFilters];
    const ywpVersionOld = String(currentIdxOldYwp || '');
    const ywpVersionNew = String(currentNewYwp || '');
    const partNumberNew = currentRow?.numberNew as string || '';
    const hasInvalidQeLevel = newRows.some((row) => {
      const qeLevel = row[FIELD_QE_LEVEL];

      return typeof qeLevel === 'string' && qeLevel.length > 0 && !isValidQeLevel(qeLevel);
    });

    if (hasInvalidQeLevel) return addToast(t('hardware.wrongQeLevelFormat'), 'Warning');

    if ((ywpVersionOld?.length > 0 && !isValidYWPFortmat(ywpVersionOld)) || (ywpVersionNew?.length > 0 && !isValidYWPFortmat(ywpVersionNew))) {
      addToast(t('dialog.YWPVersionFormat'), 'Warning', errorTypes.ywp_version_format_error);

      return;
    }

    if (partNumberNew?.length && partNumberNeedsFormat && !isValidPartNumber(partNumberNew)) {
      addToast(t('dialog.wrongPartNoFormat'), 'Warning');

      return;
    }

    if (!isTableWithBTVPerson) {
      newRows.forEach((row) => delete row[userField]);
    }

    if (isPDRS && newNumberExist) {
      setNewNumberExist(null);
    }

    if (oldNumberExist) {
      setOldNumberExist(null);
    }

    onSave(newRows);
  };

  const onNext = () => {
    const validations = getColumnValidations(columnFilters);

    if (Object.keys(validations[currentIndex]).some((elem) => validations[currentIndex][elem].invalid)) {
      setValidationActive(true);
      addToast(t('dialog.pleaseFillOut'), 'Warning', errorTypes.mandatory_fields_empty);
      setColumnValidations(validations);

      return;
    }
    const lastIndex = columnFilters?.length - 1;
    setCurrentIndex(currentIndex + 1);

    if ((currentIndex + 1) === lastIndex && Object.keys(validations[lastIndex]).some((elem) => validations[lastIndex][elem].invalid)) {
      setValidationActive(true);
      addToast(t('dialog.pleaseFillOut'), 'Warning', errorTypes.mandatory_fields_empty);
      setColumnValidations(validations);
    }
  };

  const equalsValidation = useMemo(() => {
    return !(isCodingUuidTable || isFlashingUuidTable) && newNumberExist && isMbosRelevant && isEqualPartNumbers
      ? !(currentIdxOldYwp && currentNewYwp)
      : false;
  }, [isCodingUuidTable, isFlashingUuidTable, newNumberExist, isMbosRelevant, isEqualPartNumbers, currentIdxOldYwp, currentNewYwp]);

  const onBack = () => setCurrentIndex(currentIndex - 1);

  const onDeleteRow = (index: number) => {
    const newRows = [...columnFilters];
    newRows.splice(index, 1);
    setColumnFilters(newRows);
    setCurrentIndex(((index - 1) < 0) ? 0 : (index - 1));
    setShowConfirmDeleteCR(null);
  };

  const isAddButton = (columnFilters?.length - 1) === currentIndex;
  const getInput = (elem: string, index: number) => {
    const { mbosSynchronization, dialogSynchronization } = columnFilters[index];
    const hasMBOSElementSynchronized = mbosSynchronization?.isSynchronized && mbosSynchronization?.synchronizedFields?.includes(elem);
    const isInputDisabled = isPDRSIdDisabled(elem)
      || Boolean(dialogData && (elem === NUMBER_NEW || (elem === 'modelSeries' && dialogData.codeRule?.length) || (elem === 'typeVersions' && dialogData.codeRule?.length)))
      || hasMBOSElementSynchronized;

    return ((isSoftwareTable && elem === 'ecuIsCodeable') || (!isSoftwareTable && elem === 'vpd'))
      ? (
        <Dropdown
          id={`${table}-${elem}-dropdown`}
          key={elem}
          label={createSyncFieldLabel(tableDialogTitles[elem], elem, { dialogSynchronization }, t)}
          isMandatory={inputIsMandatory(elem)}
          formControlClass={moreWidthFields.includes(elem) ? styles.Size : styles.MoreSize}
          options={elem === 'vpd' ? (formYESNO as any) : softwareForm.ecuIsCodeable.options}
          value={elem === 'vpd' ? ((columnFilters[index][elem] as string) || '') : ((columnFilters[index][elem] as string) || 'No')}
          handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleEdit(elem, ev.target.value, index)}
        />
      )
      : (
        <InputText
          invalid={validationActive && columnValidations[index]?.[elem]?.invalid}
          id={`input-dialog-${elem}`}
          key={elem}
          value={columnFilters[index][elem] as string}
          label={createSyncFieldLabel(tableDialogTitles[elem], elem, { dialogSynchronization, mbosSynchronization }, t)}
          isMandatory={inputIsMandatory(elem)}
          handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleEdit(elem, ev.target.value, index)}
          formControlClass={moreWidthFields.includes(elem) ? styles.MoreSize : styles.Size}
          placeholder={getInputPlaceHolder(elem)}
          disabled={isInputDisabled}
          helperText={elem === 'numberNew' && numberNewNotFound ? [t('dialog.partNumberNotFound')] : []}
          inputLabelClasses={{ root: classes.inputLabelTooltip }}
          maxLength={TABLE_FIELD_MAX_LENGTH[elem]}
          multiline={(TABLE_FIELD_MAX_LENGTH?.[elem] ?? 32) > 32}
        />
      );
  };

  return (
    <>
      <Dialog open maxWidth="md" onClose={handleClose} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">{t(title)}</DialogTitle>
        <DialogContent className={`${classes.content}`}>
          <div className={styles.DialogPlants}>
            {(columnFilters?.length > 1) && (
              <TabsCampaignsSubCR
                tabsLabelsConfig={columnFilters?.map((row, ind) => ({
                  label: `${row?.numberNew} (${ind + 1})`,
                }))}
                setTabSelected={setCurrentIndex}
                crSelected={currentIndex}
                onDeleteTab={setShowConfirmDeleteCR}
                cutomTabLabel={t(tableDialogTitles?.numberNew)}
                noAddButton
              />
            )}
            <SwipeableViews
              axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
              index={currentIndex}
              onChangeIndex={setCurrentIndex}
              containerStyle={{ // fix for first animation https://github.com/oliviertassinari/react-swipeable-views/issues/599
                transition: 'transform 0.95s cubic-bezier(0.15, 0.3, 0.25, 1) 0s', width: '100%',
              }}
              slideStyle={{ maxHeight: '62vh', overflowX: 'hidden', padding: '20px 5px 5px 5px', margin: '0px 0px 5px 0px', width: '100%', borderTop: 'none' }}
            >
              {columnFilters?.map((row, index) => (

                <div role="tabpanel" dir={theme.direction} className={styles.FieldsContainer} key={index}>
                  {row && Object.keys(row).filter((elem) => elem !== 'dialogSynchronization' && elem !== 'mbosSynchronization')
                    ?.map((elem) => !['btvPerson', 'uuidPersonResponsible'].includes(elem) && getInput(elem, index))}

                  {isTableWithBTVPerson && (
                    <div className={styles.BTVField}>
                      <InputUser
                        id="input-responsible-requester"
                        label={createSyncFieldLabel(
                          `dialog.${userField}`,
                          userField,
                          { dialogSynchronization: row.dialogSynchronization, mbosSynchronization: row.mbosSynchronization },
                          t,
                        )}
                        title={t(`dialog.${userField}`)}
                        updateUserSelected={(userSelected) => updateUser(userField, userSelected, index)}
                        value={row?.[userField]?.summary}
                        code={row?.[userField]?.code}
                        clear={() => updateUser(userField, null, index)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </SwipeableViews>
          </div>
        </DialogContent>

        <DialogActions>
          {(isDialogTable && !dialogData && ((isSoftwareTable && !isMbosRelevant) || !isSoftwareTable)) && (
            <Button
              color="primary"
              variant="outlined"
              disabled={((currentRow?.numberNew as string)?.length !== 11)}
              onClick={getDialogInformation}
            >
              {t('dialog.addDialogInfo')}
            </Button>
          )}
          {isMBOSTable && isMbosRelevant && (
            <Button
              color="primary"
              variant="outlined"
              onClick={setMbosFieldsInfo}
              disabled={!mbosPartNumberInfo?.description}
            >
              {t('dialog.addMBOSInfo')}
            </Button>
          )}
          {currentIndex > 0 && (
            <Button color="primary" onClick={onBack}>
              {t('dialog.back')}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={isAddButton ? onSaveRow : onNext}
            disabled={isAddButton ? (disableAddButton || equalsValidation) : false}
          >
            {t(`dialog.${isAddButton ? 'add' : 'next'}`)}
          </Button>
          <Button color="primary" onClick={close}>{t('dialog.cancel')}</Button>
        </DialogActions>
      </Dialog>

      {showConfirmDeleteCR !== null && (
        <ConfirmDialog
          close={() => setShowConfirmDeleteCR(null)}
          confirm={() => onDeleteRow(showConfirmDeleteCR)}
          title={t('dialog.deleteDataSet.title')}
          message={t('dialog.deleteDataSet.message')}
          closeBtnText={t('dialog.cancel')}
          confirmBtnText={t('dialog.confirm')}
        />
      )}

      {
        combinationSelectionOpen && (
          <DialogDataSelection
            title={t('dialog.selectDialogCombination')}
            dataToSelect={dialogData?.codeRule}
            columns={dialogDataColumns}
            columnSort="codeRule"
            onSave={saveDialogDataInfo}
            customContentStyle={classes.customContentStyle}
            onClose={() => {
              setCombinationSelectionOpen(false);
              setDialogData(null);
            }}
          />
        )
      }
    </>
  );
};

const mapStateToProps = (state) => ({
  software: getCampaignSoftware(state),
  softwareForm: getSoftwareForms(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  campaignInfo: getCampaignInfo(state),
});

export default connect(mapStateToProps)(DialogTableList);
